import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { callNonStoreApi } from "../../NonStoreApis";
import {
  GetBusinessChatHistoryApi,
  SummariseBusiness,
  UnderstandBusinessApi,
  UploadBusinessFile,
} from "../../Api/ApiEndPoints";
import { isNullOrUndefined } from "../../utils";
import Loading from "../../Loading";
import { AddFlex, CustomText, Gap } from "../../reusableStyles";
import { ArrowUp, CircleX, File, LoaderCircle, Paperclip } from "lucide-react";
import { primaryActionColor, primaryActionColorLight } from "../../constants";
import growezyAi from "../../ImageAssets/growezyArrow.svg";
import { CircularProgress } from "@mui/material";
import { enqueueSnackbar } from "notistack";

export default function AiInstaDmAutomation() {
  const { credentials } = useSelector((state) => state.instagram);
  const [messages, setMessages] = useState();
  const [input, setInput] = useState("");
  const [file, setFile] = useState(null);
  const [fileTextId, setFileTextId] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatingBusinessSummary, setGeneratingBusinessSUmmary] =
    useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const fileUploadRef = useRef(null);

  const getPreviousConversation = async () => {
    const response = await callNonStoreApi(GetBusinessChatHistoryApi, {
      instaUserId: credentials?.user_id,
    });
    const { userBusinessChatHistory } = response.data;
    setMessages(userBusinessChatHistory || []);
  };

  const sendMessage = async () => {
    try {
      if (!input.trim()) return;
      setMessageLoading(true);

      const userMessage = { content: input, role: "user", message: input };
      if (file) {
        userMessage.file = {
          fileName: file.name,
          type: file.type,
        };
      }
      setInput("");
      setFile(null);
      // make api call.
      setMessages([...messages, userMessage]);
      const response = await callNonStoreApi(UnderstandBusinessApi, {
        message: userMessage.content,
        instaUserId: credentials?.user_id,
        fileTextId,
        file,
      });
      const { aiResponse } = response.data;
      setInput("");
      setMessageLoading(false);
      setLoading(true);
      setAiMessage("");

      // Simulating AI response with typing effect
      const fullResponse = aiResponse;
      setMessages((prev) => [
        ...prev,
        { content: fullResponse, role: "assistant" },
      ]);
      //   let index = 0;

      //   const typingInterval = setInterval(() => {
      //     setAiMessage((prev) => {
      //       const nextChar = fullResponse[index];
      //       return nextChar === "\n" ? prev + "\n" : prev + nextChar;
      //     });
      //     index++;
      //     if (index === fullResponse.length) {
      //       clearInterval(typingInterval);
      //       setMessages((prev) => [
      //         ...prev,
      //         { content: fullResponse, role: "assistant" },
      //       ]);
      //       setLoading(false);
      //     }
      //   }, 10);
    } catch (error) {
      console.log(error);
    } finally {
      setMessageLoading(false);
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    // setLoading(true);
    try {
      setIsFileUploading(true);
      const file = e.target.files[0];
      setFile(file);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("instaUserId", credentials?.user_id);
      const response = await callNonStoreApi(UploadBusinessFile, formData);
      const { id } = response.data;
      setFileTextId(id);
    } catch (error) {
      console.error("Error uploading file", error);
    } finally {
      setIsFileUploading(false);
    }
    // setLoading(false);
  };

  useEffect(() => {
    getPreviousConversation();
  }, []);

  if (isNullOrUndefined(messages)) {
    return <Loading />;
  }

  return (
    <div
      className="flex flex-col bg-gray-100 p-4 mt-4"
      style={{ height: "90%" }}
    >
      <AddFlex
        flexDirection="column"
        className={
          "flex-1 overflow-auto space-y-2 p-4 bg-white rounded-xl shadow-md "
        }
        style={
          messages.length > 0
            ? { alignItems: "flex-start" }
            : { alignItems: "center", justifyContent: "center" }
        }
      >
        {messages.length > 0 ? (
          messages.map((msg, index) => (
            <div
              key={index}
              transition={{ duration: 0.3 }}
              className={`p-3 max-w-md whitespace-pre-line ${
                msg.role === "user" ? "ml-auto rounded-md" : "text-black"
              }`}
            >
              {msg.file && (
                <FileComponent
                  width="100%"
                  isFileUploading={false}
                  file={msg.file}
                />
              )}
              <div
                className={`p-3 mt-1 whitespace-pre-line ${
                  msg.role === "user"
                    ? "bg-blue-500 text-white ml-auto rounded-md"
                    : "bg-gray-100 text-black rounded-md"
                }`}
                dangerouslySetInnerHTML={{
                  __html:
                    msg.role === "user"
                      ? msg.message.replace(
                          /\*\*(.*?)\*\*/g,
                          "<strong>$1</strong>"
                        )
                      : msg.content.replace(
                          /\*\*(.*?)\*\*/g,
                          "<strong>$1</strong>"
                        ),
                }}
              ></div>
            </div>
          ))
        ) : (
          <AddFlex flexDirection="column" alignItems="center" justify="center">
            <AddFlex
              padding="10px"
              marginBottom="5px"
              backgroundColor={primaryActionColorLight}
              style={{ borderRadius: "100%" }}
            >
              <img src={growezyAi} style={{ width: "20px", height: "20px" }} />
            </AddFlex>
            <CustomText color={primaryActionColor}>
              Hey, I am Growezy AI
            </CustomText>
            <CustomText color={primaryActionColor}>
              Tell me about your business and I will help you convert leads
            </CustomText>
          </AddFlex>
        )}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="p-3 rounded-lg max-w-xs bg-gray-100 text-black whitespace-pre-line"
          >
            Typing...
          </motion.div>
        )}
      </AddFlex>
      <div className="mt-4 flex flex-col bg-white p-2 rounded-xl shadow-md">
        {file && (
          <FileComponent
            isFileUploading={isFileUploading}
            file={file}
            setFile={setFile}
          />
        )}
        <textarea
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 p-2 border-none outline-none bg-transaparent"
          style={{ width: "100%", resize: "none" }}
        />
        <AddFlex
          alignItems="center"
          gap="5px"
          style={{ borderTop: "1px solid #ececec", padding: "10px 5px" }}
        >
          {messages?.length > 1 && (
            <div
              className="text-sm p-2 pl-4 pr-4 rounded-full border border-1 border-blue-200 text-blue-500 bg-blue-100 cursor-pointer hover:bg-blue-200"
              onClick={async () => {
                try {
                  setGeneratingBusinessSUmmary(true);
                  await callNonStoreApi(SummariseBusiness, {
                    instaUserId: credentials?.user_id,
                  });
                  enqueueSnackbar("Generated business summary", {
                    variant: "success",
                  });
                } catch (error) {
                  console.log(error);
                  enqueueSnackbar("Something went wrong", {
                    variant: "error",
                  });
                } finally {
                  setGeneratingBusinessSUmmary(false);
                }
              }}
            >
              {generatingBusinessSummary
                ? "Please wait..."
                : "Generate Business Summary"}
            </div>
          )}
          <Gap />
          <div>
            <input
              ref={fileUploadRef}
              type="file"
              style={{ display: "none" }}
              accept="application/pdf"
              onChange={handleUpload}
            />
            <button
              onClick={() => {
                fileUploadRef.current.click();
              }}
              className="p-2 bg-blue-100 text-white rounded-full hover:bg-blue-200"
              style={{ width: "max-content", cursor: "pointer" }}
            >
              <Paperclip
                className="h-5 w-5"
                style={{ color: primaryActionColor }}
              />
            </button>
          </div>
          <button
            onClick={sendMessage}
            className="p-2 bg-blue-100 text-white rounded-full hover:bg-blue-200"
            style={{ width: "max-content", cursor: "pointer" }}
          >
            <ArrowUp
              className="h-5 w-5"
              style={{ color: primaryActionColor }}
            />
          </button>
        </AddFlex>
      </div>
    </div>
  );
}

const FileComponent = ({ isFileUploading, width, file, setFile }) => {
  const getFileType = (type) => {
    switch (type) {
      case "application/pdf":
        return "PDF";
      default:
        return "PDF";
    }
  };
  return (
    <AddFlex
      className="gap-2 bg-blue-100 rounded-md p-2"
      style={{ width: width ? width : "25%" }}
    >
      <AddFlex
        padding="8px"
        style={{ width: "40px", height: "40px" }}
        alignItems="center"
        justify="center"
        className={
          isFileUploading
            ? "flex rounded-md items-center bg-black-200"
            : "flex rounded-md items-center"
        }
        backgroundColor={primaryActionColor}
      >
        {isFileUploading ? (
          <CircularProgress size={12} sx={{ color: "white" }} />
        ) : (
          <File className="h-5 w-5 text-white" />
        )}
      </AddFlex>
      <AddFlex flexDirection="column" style={{ flexGrow: 1 }}>
        <CustomText
          lineClamp={1}
          style={{
            width: "90%",
            color: primaryActionColor,
            fontWeight: "500",
          }}
        >
          {file?.name || "Untitled"}
        </CustomText>
        <CustomText style={{ color: primaryActionColor }} fontSize="12px">
          {getFileType(file?.type)}
        </CustomText>
      </AddFlex>
      {setFile && (
        <AddFlex
          style={{ position: "absolute", right: "10px", top: "10px" }}
          onClick={() => {
            setFile(null);
          }}
        >
          <CircleX
            className={`h-5 w-5 cursor-pointer`}
            color={primaryActionColor}
          />
        </AddFlex>
      )}
    </AddFlex>
  );
};
